import { useEffect, useState, useCallback, useRef } from "react";
import { devLog } from "../../../utils/devLog";
import { useTranslation } from "../../../hooks/useTranslation";
import { CALLBACKS, triggerCallback } from "../../../utils/commonUtils";
import { useCheckout } from "../../../context/CheckoutContext";
import { useStyles } from "../../../context/StyleContext";
import {
  createCheckoutComApplePaySession,
  fetchGatewayConnection,
  processPayment,
} from "../../../api/api";
import { fetchSession } from "../../../utils/paymentSession";
import { useIframeResize } from "../../../hooks/useIframeResize";
import styled from "styled-components";

// ─── Styled components — UNCHANGED ───────────────────────────────────────────

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const ApplePayContainer = styled.div`
  width: 100%;
  max-width: 400px;
`;

const UnavailableMessage = styled.p`
  color: ${(props) => props.$styles?.colors?.textSecondary || "#666"};
  font-size: ${(props) => props.$styles?.typography?.fontSizes?.md || "14px"};
  font-family: ${(props) =>
    props.$styles?.typography?.fontFamily || "Arial, sans-serif"};
  text-align: center;
  margin: 0;
`;

const LoadingSpinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid
    ${(props) => props.$styles?.colors?.primary || "#2563eb"};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: ${(props) => props.$styles?.colors?.textSecondary || "#666"};
  font-size: ${(props) => props.$styles?.typography?.fontSizes?.md || "14px"};
  font-family: ${(props) =>
    props.$styles?.typography?.fontFamily || "Arial, sans-serif"};
  margin: 0;
`;

// ─────────────────────────────────────────────────────────────────────────────
// AltruonCheckoutComApplePayForm — Bridge approach
//
// RESPONSIBILITY:
//   This component is the backend brain. It owns ALL backend calls.
//   The SDK (top window) owns CheckoutWebComponents + the Apple Pay button.
//
// MESSAGE FLOW:
//
//   MOUNT
//     └─► posts "iframeReady" to parent
//           SDK receives it → sends "initApplePay" back
//
//   "initApplePay" received
//     └─► resolveGatewayConnection()
//     └─► createCheckoutComApplePaySession()   [your backend → CKO /payment-sessions]
//     └─► posts "applePaySessionReady" to parent with:
//           { paymentSession, publicKey, environment, containerSelector }
//           SDK receives it → loads CKO SDK → CheckoutWebComponents() → mounts button
//
//   User taps Apple Pay button (CKO Flow handles ApplePaySession natively)
//     └─► CKO onPaymentCompleted fires in SDK (top window)
//     └─► SDK posts "applePayToken" with CKO payment ID
//
//   "applePayToken" received
//     └─► handlePaymentCompleted()
//     └─► processPayment()                     [your backend → CKO /payments]
//     └─► posts "onSuccess" or "onFailure" to parent
//           SDK fires merchant callback (onSuccess / onFailure)
//
// WHAT IS UNCHANGED:
//   handlePaymentCompleted() — identical payload and API call
//   All styled components
//   All hooks (useIframeResize, useTranslation, useStyles, useCheckout)
//   triggerPaymentMethodEvent handling
// ─────────────────────────────────────────────────────────────────────────────

const AltruonCheckoutComApplePayForm = ({
  formData,
  itemDetails,
  billingPlatformId,
  routingEntries,
  planId,
  checkoutProcessDto,
  prefetchedGatewayConnection,
}) => {
  const queryParams = new URLSearchParams(window.location.search);
  const sessionId   = queryParams.get("session_id");

  const { t }                                       = useTranslation();
  const { styles }                                  = useStyles();
  const { triggerPaymentMethodEvent, resetTrigger, clientCountry } = useCheckout();

  const [isProcessing, setIsProcessing] = useState(false);
  const [initError,    setInitError]    = useState(null);

  const isProcessingRef      = useRef(false);
  const gatewayConnectionRef = useRef(null);
  const sessionIdRef         = useRef(sessionId);

  useIframeResize({ enabled: true, padding: 40 });

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // ── resolveGatewayConnection ─────────────────────────────────────────────
  const resolveGatewayConnection = useCallback(async () => {
    let connection = prefetchedGatewayConnection || gatewayConnectionRef.current;

    if (!connection) {
      const applePayRouting = routingEntries?.find(
        (e) => e.paymentMethod === "applepay" && e.platform === "checkoutcom"
      );
      if (!applePayRouting) {
        throw new Error("No applepay_checkoutcom routing entry found");
      }
      connection = await fetchGatewayConnection(applePayRouting, t);
      gatewayConnectionRef.current = connection;
    }

    return connection;
  }, [prefetchedGatewayConnection, routingEntries, t]);

  // ── initializeAndSendSession ─────────────────────────────────────────────
  // Called when "initApplePay" is received from the SDK.
  // Fetches gateway connection + CKO payment session, then posts
  // "applePaySessionReady" with everything the SDK needs to mount the button.
  const initializeAndSendSession = useCallback(
    async (containerSelector) => {
      setInitError(null);

      try {
        // 1. Resolve gateway connection → publicKey
        const connection = await resolveGatewayConnection();
        const publicKey  = connection?.credentials?.public_key;
        if (!publicKey) {
          throw new Error("Missing public_key in gateway connection credentials");
        }

        // 2. Create CKO payment session (your backend → CKO /payment-sessions)
        //    Identical API call to the original component — nothing changed here.
        const paymentSessionResponse = await createCheckoutComApplePaySession({
          sessionId: sessionIdRef.current,
          itemDetails,
          formData,
          clientCountry,
        });

        if (!paymentSessionResponse?.payment_session_token) {
          throw new Error("No payment_session_token returned from CKO");
        }

        // 3. Send everything to the SDK so it can call:
        //    CheckoutWebComponents({ paymentSession, publicKey, environment })
        //    and mount the Apple Pay button into containerSelector.
        //
        //    We forward containerSelector so the SDK knows exactly which DOM
        //    element to mount into — the merchant page never touches this.
        window.parent.postMessage(
          {
            type: "applePaySessionReady",
            payload: {
              paymentSession:    paymentSessionResponse, // full CKO response object
              publicKey,
              environment:       "sandbox",              // change to "production" when live
              containerSelector,                         // e.g. "#altruon-applepay-container"
              // Metadata fallback (used if CKO session amount derivation is needed)
              amount: (
                itemDetails?.amount ||
                checkoutProcessDto?.paymentData?.amount ||
                "0.00"
              ).toString(),
              currency: (
                itemDetails?.currency ||
                checkoutProcessDto?.paymentData?.currency ||
                "USD"
              ).toUpperCase(),
              label: itemDetails?.description || itemDetails?.name || "Payment",
            },
          },
          "*"
        );

        devLog("[ApplePay] applePaySessionReady posted — SDK will mount CKO button");

        // Signal the SDK iframe is ready (for other internal routing)
        window.parent.postMessage(
          { type: "formReady", data: { paymentMethod: "applepay", gateway: "checkoutcom" } },
          "*"
        );

      } catch (err) {
        console.error("[ApplePay] initializeAndSendSession failed:", err);
        setInitError(err.message || "Failed to initialize Apple Pay");

        // Tell the SDK to clear the loading state
        window.parent.postMessage(
          { type: "applePayInitFailed", payload: { error: err.message } },
          "*"
        );
      }
    },
    [resolveGatewayConnection, itemDetails, formData, checkoutProcessDto]
  );

  // ── handlePaymentCompleted — UNCHANGED from original ─────────────────────
  // Called when "applePayToken" arrives from the SDK after CKO's
  // onPaymentCompleted fires. Calls processPayment() on your backend.
  const handlePaymentCompleted = useCallback(
    async (paymentResponse, connection, userData) => {
      if (isProcessingRef.current) return;
      isProcessingRef.current = true;
      setIsProcessing(true);

      const currentSessionId = sessionIdRef.current;

      try {
        const latestSession = await fetchSession(currentSessionId);
        const sessionData   = latestSession || checkoutProcessDto;

        const payload = {
          paymentData: {
            gatewayConnectionId:
              connection?.id ||
              gatewayConnectionRef.current?.id ||
              sessionData?.paymentData?.gatewayConnectionId,
            gatewayName:   "checkoutcom",
            paymentMethod: "applepay",
            additionalData: {
              payment_method_flow: "DIRECT",
              paymentId:      paymentResponse?.id,
            },
          },
          customerData: {
            firstName: formData?.firstName || sessionData?.customerData?.firstName || userData?.customerData?.firstName,
            lastName:  formData?.lastName  || sessionData?.customerData?.lastName || userData?.customerData?.lastName,
            email:     formData?.email     || sessionData?.customerData?.email || userData?.customerData?.email,
            phone:     formData?.phone     || sessionData?.customerData?.phone || userData?.customerData?.phone,
          },
          billingData: {
            planId,
            billingPlatformId,
            billingPlatformName:
              itemDetails?.billingPlatformName ||
              sessionData?.billingData?.billingPlatformName ||
              "",
            frequency: sessionData?.billingData?.frequency || "",
          },
        };

        const response = await processPayment(billingPlatformId, currentSessionId, payload);

        if (response.status === 200) {
          const transactionId = response.data?.transactionId || paymentResponse.id;

          triggerCallback(CALLBACKS.SUCCESS, {
            status:        "success",
            transactionId,
            paymentMethod: "applepay",
            message:
              t("messages.success.paymentComplete") ||
              "Payment successfully completed.",
          });

          // SDK global listener receives "onSuccess" and fires the merchant callback.
          // CKO Flow has already called completePayment() internally — we don't need to.
          window.parent.postMessage(
            { type: "onSuccess", payload: { transactionId, paymentMethod: "applepay" } },
            "*"
          );
        } else {
          throw new Error(response.data?.message || "Payment processing failed");
        }
      } catch (err) {
        console.error("[ApplePay] Payment completion error:", err);

        const errorMessage =
          err.response?.data?.error?.message ||
          err.response?.data?.message ||
          err.message ||
          t("messages.error.paymentFailed") ||
          "Failed to process Apple Pay payment";

        const errorCode = err.response?.data?.error?.code || "PAYMENT_ERROR";

        triggerCallback(CALLBACKS.FAILURE, {
          status: "failed",
          error:  { code: errorCode, message: errorMessage, paymentMethod: "applepay" },
        });

        window.parent.postMessage(
          {
            type:    "onFailure",
            payload: { error: { code: errorCode, message: errorMessage, paymentMethod: "applepay" } },
          },
          "*"
        );
      } finally {
        isProcessingRef.current = false;
        setIsProcessing(false);
      }
    },
    [formData, planId, billingPlatformId, itemDetails, checkoutProcessDto, t]
  );

  // ── postMessage listener + iframeReady signal ────────────────────────────
  // KEY FIX: "iframeReady" is posted at the end of this useEffect, AFTER
  // addEventListener() is called. This guarantees the listener is live before
  // the SDK sends "initApplePay". Previously the SDK sent "initApplePay" via
  // MutationObserver (iframe DOM exists ≠ React is mounted and listening),
  // causing the message to fire into the void and the button to never appear.
  useEffect(() => {
    const handleMessage = async (event) => {
      const type = event.data?.type;

      // ── SDK requests CKO session initialization ──────────────────────────
      if (type === "initApplePay") {
        devLog("[ApplePay] initApplePay received — fetching CKO session...");
        const { containerSelector } = event.data.payload || {};
        await initializeAndSendSession(containerSelector);
        return;
      }

      // ── CKO onPaymentCompleted forwarded by SDK ──────────────────────────
      if (type === "applePayToken") {
        const applePayToken = event.data.payload?.applePayToken;
        const userData = event.data.payload?.userData;
        if (!applePayToken) {
          console.error("[ApplePay] applePayToken missing in message payload");
          return;
        }
        devLog("[ApplePay] applePayToken received — processing payment...");
        await handlePaymentCompleted(
          {
            id:          applePayToken.ckoPaymentId || applePayToken.transactionIdentifier,
            applePayToken,
          },
          gatewayConnectionRef.current,
          userData,
        );
        return;
      }

      // ── Other payment methods use makePayment (unchanged) ────────────────
      if (type === "makePayment") {
        console.warn(
          "[ApplePay] makePayment received — Apple Pay uses CKO Flow button in " +
          "the top window, not makePayment. Ignoring."
        );
        return;
      }
    };

    window.addEventListener("message", handleMessage);

    // ── iframeReady signal ───────────────────────────────────────────────────
    // Posted HERE — after addEventListener() — so the SDK knows the listener
    // is live and safe to send "initApplePay". This is the fix for the timing
    // bug where "Loading Apple Pay..." was shown forever.
    devLog("[ApplePay] Component mounted — posting iframeReady to SDK");
    window.parent.postMessage({ type: "iframeReady" }, "*");

    return () => window.removeEventListener("message", handleMessage);
  }, [initializeAndSendSession, handlePaymentCompleted]);

  // ── triggerPaymentMethodEvent — UNCHANGED ────────────────────────────────
  useEffect(() => {
    if (triggerPaymentMethodEvent && !isProcessingRef.current) {
      resetTrigger();
    }
  }, [triggerPaymentMethodEvent, resetTrigger]);

  // ── Render ───────────────────────────────────────────────────────────────
  // This component renders nothing visible in the iframe.
  // The Apple Pay button lives in the top window, mounted by the SDK.
  // We only show errors or a processing spinner when relevant.

  if (initError) {
    return (
      <Container>
        <UnavailableMessage $styles={styles}>
          {t("checkout.paymentMethods.applepay.error") || initError}
        </UnavailableMessage>
      </Container>
    );
  }

  if (isProcessing) {
    return (
      <Container>
        <LoadingSpinner $styles={styles} />
        <LoadingText $styles={styles}>
          {t("checkout.paymentMethods.applepay.processing") ||
            "Processing Apple Pay payment..."}
        </LoadingText>
        <ApplePayContainer id="applepay-container" />
      </Container>
    );
  }

  // Idle — listening for messages, button is in the top window
  return <ApplePayContainer id="applepay-container" />;
};

export default AltruonCheckoutComApplePayForm;